import { TableBody, TableCell, TableRow, Checkbox, Skeleton, toCurrencyString, CurrencyCode } from '@client/toolkit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChangePercentage } from '../../components/change-percentage';
import { ExpanableTableWraper, SortDirection } from '../../components/expanable-table-wraper';
import { TableExpandableCell } from '../../components/table-expanable-cell';
import { BuildingIncomeData } from '@client/api';
import { generatePath, useNavigate } from 'react-router-dom';
import { PortfolioPaths } from '../../../portfolio/portfolio.router';
import { MdOpenInNew } from 'react-icons/md';

export const BuildingRentalTable = ({
  data = [],
  baseYear,
  comparisonYear,
  loading,
  sortColumn,
  sortDirection,
  onSelected,
  onSort,
}: {
  data: BuildingIncomeData[];
  baseYear: number;
  comparisonYear: number;
  loading: boolean;
  sortColumn?: string;
  sortDirection?: SortDirection;
  onSelected: (ids: Set<string>) => void;
  onSort: (col: string, direction: SortDirection) => void;
}) => {
  const { t } = useTranslation('reporting');
  const navigate = useNavigate();

  const columns = [
    { key: 'buildingName', label: t('rental.building'), isSortable: true, className: 'w-[25%]' },
    { key: 'tenants', label: t('rental.tenants'), isSortable: false, className: 'w-[25%]' },
    { key: 'baseYear', label: t('rental.baseYear', { year: baseYear }), isSortable: false, className: 'w-[13%]' },
    {
      key: 'comparisonYear',
      label: t('rental.comparisonYear', { year: comparisonYear }),
      isSortable: false,
      className: 'w-[13%]',
    },
    { key: 'diff', label: t('rental.diff', { year: baseYear }), isSortable: false, className: 'w-[13%]' },
    { key: 'trend', label: t('rental.trend'), isSortable: false, className: 'w-[10%]' },
  ];

  return (
    <ExpanableTableWraper
      loading={loading}
      onSort={onSort}
      onSelected={onSelected}
      columns={columns}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      data={data}
      renderBody={(
        data,
        selectedRows,
        expandedRows,
        handleToggleSelect,
        handleKeyDownSelect,
        handleToggleExpand,
        handleKeyDownExpand,
      ) => (
        <TableBody>
          {data.map((building) => (
            <React.Fragment key={building.buildingId}>
              <TableRow
                className="bg-surface-elevated/40 border-b border-surface-elevated group cursor-pointer hover:bg-surface-elevated"
                onClick={() =>
                  navigate(generatePath(PortfolioPaths.BuildingAgreements, { buildingId: building.buildingId }))
                }
              >
                <TableCell className="px-2 py-2 align-top content-center">
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    checked={selectedRows.has(building.buildingId)}
                    onChange={() => handleToggleSelect(building.buildingId)}
                    tabIndex={0}
                    aria-label={t('table.selectEntity', { entity: building.buildingName })}
                    onKeyDown={(e) => handleKeyDownSelect(e, building.buildingId)}
                  />
                </TableCell>
                <TableExpandableCell
                  label={building.buildingName}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand(building.buildingId);
                  }}
                  onKeyDown={(e) => handleKeyDownExpand(e, building.buildingId)}
                  ariaLabel={
                    expandedRows.has(building.buildingId)
                      ? t('table.collapsedEntity', { entity: building.buildingName })
                      : t('table.expandedEntity', { entity: building.buildingName })
                  }
                  isExpanded={expandedRows.has(building.buildingId)}
                  icon={<MdOpenInNew className="text-brand-primary" />}
                />
                <TableCell className="px-2 py-2 align-top">
                  {t('table.numberOfTenants', { count: building.tenantIncomes.length })}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">
                  {toCurrencyString(building.currentPeriodIncome.value, '', {
                    currency: building.currentPeriodIncome.currency as CurrencyCode,
                    minimumFractionDigits: Number.isInteger(building.currentPeriodIncome.value) ? 0 : 2,
                  })}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">
                  {toCurrencyString(building.comparisonPeriodIncome.value, '', {
                    currency: building.comparisonPeriodIncome.currency as CurrencyCode,
                    minimumFractionDigits: Number.isInteger(building.comparisonPeriodIncome.value) ? 0 : 2,
                  })}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">
                  {toCurrencyString(building.variance.absolute.value, '', {
                    currency: building.variance.absolute.currency as CurrencyCode,
                    minimumFractionDigits: Number.isInteger(building.variance.absolute.value) ? 0 : 2,
                  })}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">
                  <ChangePercentage value={building.variance.percentage} />
                </TableCell>
              </TableRow>
              {expandedRows.has(building.buildingId) &&
                building.tenantIncomes.map((tenant) => (
                  <React.Fragment key={tenant.tenantId}>
                    <TableRow className="bg-surface-base border-b border-surface-elevated">
                      <TableCell className="px-2 py-2"></TableCell>
                      <TableCell className="px-2 py-2"></TableCell>
                      <TableCell className="px-2 py-2 align-top">{tenant.tenantName}</TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {toCurrencyString(tenant.currentPeriodIncome.value, '', {
                          currency: tenant.currentPeriodIncome.currency as CurrencyCode,
                          minimumFractionDigits: Number.isInteger(tenant.currentPeriodIncome.value) ? 0 : 2,
                        })}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {toCurrencyString(tenant.comparisonPeriodIncome.value, '', {
                          currency: tenant.comparisonPeriodIncome.currency as CurrencyCode,
                          minimumFractionDigits: Number.isInteger(tenant.comparisonPeriodIncome.value) ? 0 : 2,
                        })}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {toCurrencyString(tenant.variance.absolute.value, '', {
                          currency: tenant.variance.absolute.currency as CurrencyCode,
                          minimumFractionDigits: Number.isInteger(tenant.variance.absolute.value) ? 0 : 2,
                        })}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        <ChangePercentage value={tenant.variance.percentage} />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
            </React.Fragment>
          ))}
        </TableBody>
      )}
    />
  );
};
